/**
 * Welcome Step
 * 
 * Contextual welcome message based on how user arrived.
 * Issue #563 - First-Run Onboarding Flow
 */

import type { Me, OnboardingContext } from '@/lib/api/auth';

interface WelcomeStepProps {
  user: Me;
  context: OnboardingContext;
  onNext: () => void;
}

export default function WelcomeStep({ user, context, onNext }: WelcomeStepProps) {
  // Contextual title and subtitle
  const getContent = () => {
    switch (context.type) {
      case 'club':
        return {
          title: `Welcome to ${context.club_name}`,
          subtitle: 'You are joining a community built on movement and connection.',
        };
      case 'event':
        return {
          title: `You are set for ${context.event_name}`,
          subtitle: 'Let us get you oriented before the event.',
        };
      case 'founder':
        return {
          title: 'Welcome, Founder',
          subtitle: 'Let us build your community together.',
        };
      case 'clean':
      default:
        return {
          title: 'Welcome to ORO MOVE',
          subtitle: 'Let us get your movement profile ready.',
        };
    }
  };

  const { title, subtitle } = getContent();
  const firstName = user.first_name || 'there';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 
          id="onboarding-title"
          className="text-2xl font-semibold text-[--oro-text-primary]"
        >
          {title}
        </h2>
        <p className="text-[--oro-text-secondary]">
          Hi {firstName}! {subtitle}
        </p>
      </div>

      {/* Illustration placeholder - 3-Layer CSS semantic container */}
      <div className="h-48 rounded-xl bg-[--oro-surface-secondary] flex items-center justify-center">
        <span className="text-[--oro-text-tertiary] text-sm">
          Welcome illustration
        </span>
      </div>

      {/* CTA */}
      <div className="flex justify-end">
        <button
          onClick={onNext}
          className="px-6 py-2.5 rounded-lg bg-[--oro-accent-primary] text-[--oro-text-on-accent] font-medium hover:bg-[--oro-accent-hover] transition-colors"
        >
          Get Started
        </button>
      </div>
    </div>
  );
}
